import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTrainingSocket } from '../../context/TrainingSocketContext';
import './ReevaluationModal.css';

interface ModelEvaluationInfo {
  epoch: number;
  train_loss: number;
  val_loss: number;
  learning_rate?: number;
  gradient_norm?: number;
  model_structure?: any;
  evaluation_analysis?: {
    plateau_detected: boolean;
    divergence_detected: boolean;
    gradient_issue: boolean;
    lr_exhausted: boolean;
    plateau_severity?: number;
    divergence_severity?: number;
    gradient_health?: string;
  };
  iteration_history?: {
    current_iteration: number;
    regeneration_count: number;
    max_regenerations: number;
  };
}

interface ReevaluationModalProps {
  onClose: () => void;
}

const ReevaluationModal: React.FC<ReevaluationModalProps> = ({ onClose }) => {
  const { 
    trainingProgressData, 
    modelStructure,
    modelEvaluationInfo,
    requestModelEvaluationInfo,
    triggerManualReevaluation 
  } = useTrainingSocket();
  
  const [evaluationInfo, setEvaluationInfo] = useState<ModelEvaluationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [reevaluating, setReevaluating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    // Request evaluation info when modal opens
    if (requestModelEvaluationInfo) {
      requestModelEvaluationInfo();
      setLoading(true);
    }
  }, [requestModelEvaluationInfo]);

  // Listen for evaluation info from backend
  useEffect(() => {
    if (modelEvaluationInfo && modelEvaluationInfo.success && modelEvaluationInfo.data) {
      setEvaluationInfo(modelEvaluationInfo.data);
      setLoading(false);
    } else if (modelEvaluationInfo && !modelEvaluationInfo.success) {
      setError(modelEvaluationInfo.message || 'Failed to load evaluation info');
      setLoading(false);
    }
  }, [modelEvaluationInfo]);

  // Fallback: Use latest training progress if evaluation info not available
  useEffect(() => {
    if (!evaluationInfo && trainingProgressData.length > 0) {
      const latestData = trainingProgressData[trainingProgressData.length - 1];
      setEvaluationInfo({
        epoch: latestData.epoch,
        train_loss: latestData.train_loss,
        val_loss: latestData.val_loss,
        model_structure: modelStructure,
      });
      setLoading(false);
    }
  }, [trainingProgressData, modelStructure, evaluationInfo]);

  const handleReevaluate = async () => {
    if (!triggerManualReevaluation) {
      setError('Reevaluation service not available');
      return;
    }

    setReevaluating(true);
    setError(null);
    setSuccessMessage(null);

    try {
      triggerManualReevaluation();
      setSuccessMessage('Reevaluation request sent. The model will be evaluated at the next epoch.');
      // Reset after a delay
      setTimeout(() => {
        setReevaluating(false);
      }, 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to trigger reevaluation');
      setReevaluating(false);
    }
  };

  const formatNumber = (num: number | undefined, decimals: number = 4): string => {
    if (num === undefined || num === null) return 'N/A';
    return num.toFixed(decimals);
  };

  const formatScientific = (num: number | undefined): string => {
    if (num === undefined || num === null) return 'N/A';
    return num.toExponential(2);
  };

  const getModelSummary = () => {
    if (!modelStructure || !modelStructure.layers) return 'No model structure available';
    const layerCount = modelStructure.layers.length;
    const totalParams = modelStructure.total_parameters || 'Unknown';
    return `${layerCount} layers, ${totalParams} parameters`;
  };

  return (
    <motion.div
      className="reevaluation-modal"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.3 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="reevaluation-modal-content">
        <h2 className="reevaluation-modal-title">Model Reevaluation</h2>
        <p className="reevaluation-modal-subtitle">Review current model status and trigger manual reevaluation</p>

        {loading && !evaluationInfo && (
          <div className="reevaluation-loading">
            <div className="reevaluation-spinner"></div>
            <p>Loading model information...</p>
          </div>
        )}

        {error && (
          <div className="reevaluation-error">
            <p>⚠️ {error}</p>
          </div>
        )}

        {successMessage && (
          <div className="reevaluation-success">
            <p>✓ {successMessage}</p>
          </div>
        )}

        {evaluationInfo && (
          <div className="reevaluation-info">
            <div className="reevaluation-section">
              <h3 className="reevaluation-section-title">Current Training Metrics</h3>
              <div className="reevaluation-metrics-grid">
                <div className="reevaluation-metric">
                  <span className="reevaluation-metric-label">Epoch</span>
                  <span className="reevaluation-metric-value">{evaluationInfo.epoch}</span>
                </div>
                <div className="reevaluation-metric">
                  <span className="reevaluation-metric-label">Training Loss</span>
                  <span className="reevaluation-metric-value">{formatNumber(evaluationInfo.train_loss)}</span>
                </div>
                <div className="reevaluation-metric">
                  <span className="reevaluation-metric-label">Validation Loss</span>
                  <span className="reevaluation-metric-value">{formatNumber(evaluationInfo.val_loss)}</span>
                </div>
                {evaluationInfo.learning_rate !== undefined && (
                  <div className="reevaluation-metric">
                    <span className="reevaluation-metric-label">Learning Rate</span>
                    <span className="reevaluation-metric-value">{formatScientific(evaluationInfo.learning_rate)}</span>
                  </div>
                )}
                {evaluationInfo.gradient_norm !== undefined && (
                  <div className="reevaluation-metric">
                    <span className="reevaluation-metric-label">Gradient Norm</span>
                    <span className="reevaluation-metric-value">{formatNumber(evaluationInfo.gradient_norm)}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="reevaluation-section">
              <h3 className="reevaluation-section-title">Model Architecture</h3>
              <div className="reevaluation-architecture">
                <p>{getModelSummary()}</p>
              </div>
            </div>

            {evaluationInfo.evaluation_analysis && (
              <div className="reevaluation-section">
                <h3 className="reevaluation-section-title">Evaluation Analysis</h3>
                <div className="reevaluation-analysis">
                  {evaluationInfo.evaluation_analysis.plateau_detected && (
                    <div className="reevaluation-analysis-item warning">
                      ⚠️ Plateau detected (severity: {formatNumber(evaluationInfo.evaluation_analysis.plateau_severity, 2)})
                    </div>
                  )}
                  {evaluationInfo.evaluation_analysis.divergence_detected && (
                    <div className="reevaluation-analysis-item error">
                      ⚠️ Divergence detected (severity: {formatNumber(evaluationInfo.evaluation_analysis.divergence_severity, 2)})
                    </div>
                  )}
                  {evaluationInfo.evaluation_analysis.gradient_issue && (
                    <div className="reevaluation-analysis-item warning">
                      ⚠️ Gradient issue: {evaluationInfo.evaluation_analysis.gradient_health}
                    </div>
                  )}
                  {evaluationInfo.evaluation_analysis.lr_exhausted && (
                    <div className="reevaluation-analysis-item warning">
                      ⚠️ Learning rate exhausted
                    </div>
                  )}
                  {!evaluationInfo.evaluation_analysis.plateau_detected &&
                   !evaluationInfo.evaluation_analysis.divergence_detected &&
                   !evaluationInfo.evaluation_analysis.gradient_issue &&
                   !evaluationInfo.evaluation_analysis.lr_exhausted && (
                    <div className="reevaluation-analysis-item success">
                      ✓ No issues detected
                    </div>
                  )}
                </div>
              </div>
            )}

            {evaluationInfo.iteration_history && (
              <div className="reevaluation-section">
                <h3 className="reevaluation-section-title">Reevaluation History</h3>
                <div className="reevaluation-history">
                  <p>
                    Iteration: {evaluationInfo.iteration_history.current_iteration} / 
                    Regenerations: {evaluationInfo.iteration_history.regeneration_count} / 
                    Max: {evaluationInfo.iteration_history.max_regenerations}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="reevaluation-modal-buttons">
          <button
            type="button"
            onClick={onClose}
            className="reevaluation-btn reevaluation-btn-cancel"
            disabled={reevaluating}
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleReevaluate}
            className="reevaluation-btn reevaluation-btn-reevaluate"
            disabled={reevaluating || loading}
          >
            {reevaluating ? (
              <>
                <span className="reevaluation-spinner-small"></span>
                Processing...
              </>
            ) : (
              'Reevaluate Model'
            )}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default ReevaluationModal;

